import { defineTour } from '../../types';

/**
 * T04 — Review an assigned approval (brief §7).
 *
 * Eligible exactly when the Approvals entry is: the server's effective
 * authority (`canReach('approvals')`), which is how a delegate — who holds no
 * standing approval permission — reaches their work. The inbox is
 * `/approvals` (GET only). Every other step is on a request the person opened
 * from it. The guide ends on the decision: choosing one opens its own
 * confirmation, which the person completes.
 *
 * Copy: `guidance.tours.T04.*` — draft, for native-language review.
 */
const RECORD = {
  routeId: 'tenant.approval',
  explainKey: 'guidance.tours.T04.openApproval',
} as const;

export const T04 = defineTour({
  id: 'T04',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T04.title',
  summaryKey: 'guidance.tours.T04.summary',
  eligible: (ctx) => ctx.portal === 'tenant' && ctx.canReach('approvals'),
  pageRoutes: ['tenant.approvals', 'tenant.approval'],
  steps: [
    {
      id: 't04-inbox',
      anchor: 'tenant.approvals.inbox',
      route: 'tenant.approvals',
      titleKey: 'guidance.tours.T04.inbox.title',
      bodyKey: 'guidance.tours.T04.inbox.body',
    },
    {
      id: 't04-why',
      anchor: 'tenant.approval.why',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T04.why.title',
      bodyKey: 'guidance.tours.T04.why.body',
    },
    {
      id: 't04-lines',
      anchor: 'tenant.approval.lines',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T04.lines.title',
      bodyKey: 'guidance.tours.T04.lines.body',
    },
    {
      id: 't04-budget',
      anchor: 'tenant.approval.budget',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T04.budget.title',
      bodyKey: 'guidance.tours.T04.budget.body',
    },
    {
      id: 't04-workflow',
      anchor: 'tenant.approval.workflow',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T04.workflow.title',
      bodyKey: 'guidance.tours.T04.workflow.body',
    },
    {
      // From 640px the decision panel (inline on a tablet, beside the record
      // on a desk; CSS shows one); below it, the phone's Decide button.
      id: 't04-decide',
      anchor: ['tenant.approval.decision-panel', 'tenant.approval.decide-phone'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T04.decide.title',
      bodyKey: 'guidance.tours.T04.decide.body',
      commitNote: true,
    },
  ],
});
